/**
 * DesignOLTNode - Editable OLT node for network design
 */
import React, { useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';

export interface DesignOLTNodeData {
  label: string;
  power: number; // dBm
  ports?: number;
}

export const DesignOLTNode: React.FC<NodeProps> = ({ id, data: nodeData, selected }) => {
  const data = nodeData as unknown as DesignOLTNodeData;
  const { updateNodeData, deleteElements } = useReactFlow();
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelValue, setLabelValue] = useState(data.label);

  const handleLabelChange = () => {
    updateNodeData(id as string, {
      ...data,
      label: labelValue,
    });
    setIsEditingLabel(false);
  };

  const handlePowerChange = (newPower: number) => {
    updateNodeData(id as string, {
      ...data,
      power: newPower,
    });
  };

  const handlePortsChange = (newPorts: number) => {
    updateNodeData(id as string, {
      ...data,
      ports: Math.max(1, Math.min(64, newPorts)), // Min 1, Max 64
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Delete ${data.label}?`)) {
      deleteElements({ nodes: [{ id: id as string }] });
    }
  };

  return (
    <div className={`design-node design-olt-node ${selected ? 'selected' : ''}`}>
      <div className="design-node-header">
        <span>🏢</span>
        {isEditingLabel ? (
          <input
            type="text"
            value={labelValue}
            onChange={(e) => setLabelValue(e.target.value)}
            onBlur={handleLabelChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLabelChange();
              if (e.key === 'Escape') {
                setLabelValue(data.label);
                setIsEditingLabel(false);
              }
            }}
            autoFocus
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              outline: 'none',
              width: '120px',
            }}
          />
        ) : (
          <span
            onDoubleClick={() => setIsEditingLabel(true)}
            style={{ cursor: 'text' }}
            title="Dvojklik na úpravu"
          >
            {data.label}
          </span>
        )}
        <button
          onClick={handleDelete}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '2px 4px',
            marginLeft: 'auto',
          }}
          title="Zmazať OLT"
        >
          🗑️
        </button>
      </div>
      <div className="design-node-body">
        <div className="design-field">
          <span className="design-field-label">Power:</span>
          <input
            type="number"
            className="design-field-input"
            value={data.power}
            step="0.1"
            onChange={(e) => handlePowerChange(parseFloat(e.target.value) || 0)}
            onKeyDown={(e) => e.stopPropagation()}
          />
          <span className="design-field-value">dBm</span>
        </div>
        <div className="design-field">
          <span className="design-field-label">Porty:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => handlePortsChange((data.ports || 4) - 1)}
              style={{
                background: '#f44336',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '2px 6px',
                borderRadius: '3px',
              }}
              title="Odobrať port"
            >
              −
            </button>
            <span className="design-field-value" style={{ minWidth: '30px', textAlign: 'center' }}>
              {data.ports || 4}
            </span>
            <button
              onClick={() => handlePortsChange((data.ports || 4) + 1)}
              style={{
                background: '#4caf50',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '2px 6px',
                borderRadius: '3px',
              }}
              title="Pridať port"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Output handles for each port */}
      {Array.from({ length: data.ports || 4 }).map((_, index) => (
        <Handle
          key={index}
          type="source"
          position={Position.Right}
          id={`port-${index + 1}`}
          style={{
            top: `${((index + 1) / ((data.ports || 4) + 1)) * 100}%`,
            background: '#4caf50',
          }}
          className="design-handle source-handle"
          title={`Port ${index + 1}: ${data.power.toFixed(2)} dBm`}
        />
      ))}
    </div>
  );
};

